import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyButton,
  Input,
  Skeleton,
} from '@tinct/web-components';
import {
  createSsoDomainSchema,
  ReadSsoDomain,
  SsoDomainStatus,
} from '@tinct/core-api-models';
import {
  useCreateSsoDomain,
  useDeleteSsoDomain,
  useVerifySsoDomain,
} from '@tinct/core-api';
import { GlobeIcon, RefreshCcwIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { RemoveSsoDomainModal } from '../modals/remove-sso-domain-modal';
import { SECURITY_CARD_ANCHORS } from '../security-card-anchors';

const statusVariants: Record<
  SsoDomainStatus,
  'success' | 'warning' | 'destructive'
> = {
  verified: 'success',
  pending: 'warning',
  suspended: 'destructive',
};

const statusLabels: Record<SsoDomainStatus, string> = {
  verified: 'Verified',
  pending: 'Pending',
  suspended: 'Record missing',
};

export type SsoVerifiedDomainsCardProps = {
  organizationId: string;
  domains: ReadSsoDomain[];
  isLoading: boolean;
  /** The connection lets people in today: removing the last verified domain stops that. */
  isConnectionActive: boolean;
};

/**
 * The e-mail domains this workspace claims, each proven by a DNS TXT record. A domain is added and
 * verified, never edited, so there is one input and one action per row.
 */
export function SsoVerifiedDomainsCard({
  organizationId,
  domains,
  isLoading,
  isConnectionActive,
}: SsoVerifiedDomainsCardProps) {
  const [domain, setDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  // The API's refusal belongs to the row it was asked about: which record is missing, or that the
  // DNS did not answer at all, is what tells the admin what to do next.
  const [verifyError, setVerifyError] = useState<{
    domainId: string;
    message: string;
  } | null>(null);
  const [domainToRemove, setDomainToRemove] = useState<ReadSsoDomain | null>(
    null,
  );

  const createDomain = useCreateSsoDomain({
    onSuccess: () => {
      setDomain('');
      toast.success('Domain added — publish the record below, then verify');
    },
  });

  const verifyDomain = useVerifySsoDomain({
    onSuccess: (verified) => {
      setVerifyError(null);
      toast.success(`${verified.domain} is verified`);
    },
  });

  const deleteDomain = useDeleteSsoDomain({
    onSuccess: () => {
      setDomainToRemove(null);
      toast.success('Domain removed');
    },
    onError: (error) => toast.error(error.error),
  });

  const handleAdd = () => {
    const parsed = createSsoDomainSchema.safeParse({
      domain: domain.trim().toLowerCase(),
    });
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Invalid domain');
      return;
    }
    setValidationError(null);
    createDomain.mutate({ organizationId, data: parsed.data });
  };

  const handleVerify = (toVerify: ReadSsoDomain) => {
    if (!toVerify.id) return;
    const domainId = toVerify.id;
    setVerifyError(null);
    verifyDomain.mutate(
      { organizationId, domainId },
      {
        onError: (error) => setVerifyError({ domainId, message: error.error }),
      },
    );
  };

  const verifiedCount = domains.filter(
    (row) => row.status === 'verified',
  ).length;
  const addError = validationError ?? createDomain.error?.error ?? null;

  return (
    <Card id={SECURITY_CARD_ANCHORS.ssoVerifiedDomains} tabIndex={-1}>
      <CardHeader>
        <CardTitle className="text-xl">Verified domains</CardTitle>
        <CardDescription>
          Only addresses on a domain you have proven can sign in through your
          identity provider. Publish the TXT record we give you with your DNS
          provider, then verify it.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex items-start gap-3">
          <div className="flex flex-1 flex-col gap-1">
            <div className="relative">
              <GlobeIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && handleAdd()}
                placeholder="acme.com"
                aria-label="Domain to claim"
                className="pl-9"
              />
            </div>
            {addError && (
              <p className="text-sm text-destructive">{addError}</p>
            )}
          </div>
          <Button
            onClick={handleAdd}
            disabled={!domain.trim()}
            isLoading={createDomain.isPending}
          >
            Add domain
          </Button>
        </div>

        {isLoading && <Skeleton className="h-24 w-full rounded-xl" />}

        {!isLoading && domains.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No domain claimed yet. Single sign-on cannot be enabled until one is
            verified.
          </p>
        )}

        {!isLoading &&
          domains.map((row) => (
            <div
              key={row.id}
              className="flex flex-col gap-3 rounded-2xl border p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{row.domain}</span>
                  {row.status && (
                    <Badge variant={statusVariants[row.status]}>
                      {statusLabels[row.status]}
                    </Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {row.status !== 'verified' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleVerify(row)}
                      isLoading={
                        verifyDomain.isPending &&
                        verifyDomain.variables?.domainId === row.id
                      }
                    >
                      <RefreshCcwIcon className="size-4" />
                      Verify
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDomainToRemove(row)}
                    aria-label={`Remove ${row.domain}`}
                  >
                    <Trash2Icon className="size-4 text-destructive" />
                  </Button>
                </div>
              </div>

              {row.status === 'verified' ? (
                <p className="text-sm text-muted-foreground">
                  Keep the record published: we check it every day, and a domain
                  whose record is gone stops routing to your identity provider.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    {row.status === 'suspended'
                      ? 'The record is no longer visible. Publish it again and verify — the domain comes back on its own once we see it.'
                      : 'Publish this record with your DNS provider, then verify. It can take a few minutes to propagate.'}
                  </p>
                  <div className="flex flex-col gap-1 rounded-lg bg-muted/50 p-3 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-muted-foreground">
                        Type
                      </span>
                      <span className="font-mono">
                        {row.verificationRecordType ?? 'TXT'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-muted-foreground">
                        Name
                      </span>
                      <span className="break-all font-mono">
                        {row.verificationRecordName}
                      </span>
                      {row.verificationRecordName && (
                        <CopyButton value={row.verificationRecordName} />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-muted-foreground">
                        Value
                      </span>
                      <span className="break-all font-mono">
                        {row.verificationRecordValue}
                      </span>
                      {row.verificationRecordValue && (
                        <CopyButton value={row.verificationRecordValue} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {verifyError?.domainId === row.id && (
                <p className="text-sm text-destructive">
                  {verifyError.message}
                </p>
              )}
            </div>
          ))}
      </CardContent>

      {domainToRemove && (
        <RemoveSsoDomainModal
          open
          onOpenChange={(open) => !open && setDomainToRemove(null)}
          domain={domainToRemove.domain ?? ''}
          // The connection falls back to "tested" — nobody signs in through it any more.
          isLastVerifiedOfActiveConnection={
            isConnectionActive &&
            domainToRemove.status === 'verified' &&
            verifiedCount === 1
          }
          isRemoving={deleteDomain.isPending}
          onConfirm={() =>
            domainToRemove.id &&
            deleteDomain.mutate({ organizationId, domainId: domainToRemove.id })
          }
        />
      )}
    </Card>
  );
}
